import { useMemo, useState } from "react";
import { Avatar, brandGradient, ChatGlyph, SparkIcon, SendIcon, SearchIcon } from "./ui";
import ScopeSelect from "./ScopeSelect";

/**
 * Pane B when no thread is open — the space that used to be a lonely icon on a
 * 900px-wide canvas. Two modes:
 *   • "contacts" → the New-message screen: a live preview of the highlighted
 *     member, or a people grid to pick from.
 *   • "empty"    → the inbox landing: greeting + suggested people to message.
 */
export default function DiscoverPane({
  mode,
  primary,
  member,
  contacts = [],
  conversations = [],
  onlineIds,
  previewContact,
  onStartChat,
  onOpenContacts,
  contactGroups = [],
  contactCourses = [],
  onScopeStart,
}) {
  // Which list the landing shortcut offers. Same Courses|Groups split the
  // Members page and the New-message picker use.
  const [scopeMode, setScopeMode] = useState("courses");
  // People you haven't messaged yet, online first — the useful half of the list.
  const suggestions = useMemo(() => {
    const known = new Set(conversations.map((c) => c.member_token));
    const fresh = contacts.filter((c) => !known.has(c.member_token));
    const pool = fresh.length ? fresh : contacts;
    return [...pool]
      .sort((a, b) => Number(onlineIds.has(Number(b.other_id))) - Number(onlineIds.has(Number(a.other_id))))
      .slice(0, 9);
  }, [contacts, conversations, onlineIds]);

  /* ------------------------------------------ single-member preview card */
  if (mode === "contacts" && previewContact) {
    const c = previewContact;
    const online = onlineIds.has(Number(c.other_id));
    return (
      <div className="chat-fade flex h-full flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm overflow-hidden rounded-3xl border border-slate-200/80 bg-surface shadow-[0_24px_60px_-30px_rgba(15,23,42,0.45)]">
          <div className="h-24" style={{ background: `linear-gradient(135deg, ${primary}2e 0%, ${primary}08 100%)` }} />
          <div className="-mt-12 flex flex-col items-center px-6 pb-6 text-center">
            <Avatar name={c.name} img={c.img} primary={primary} size={92} gradientRing online={online} />
            <p className="mt-3 text-xl font-extrabold tracking-tight text-slate-900">{c.name}</p>
            <p className="mt-0.5 text-[12px] font-semibold text-slate-400">
              {online ? <span className="text-emerald-500">Active now</span> : "Member of this membership"}
            </p>

            {(c.level != null || c.points != null || c.location) && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                {c.level != null && (
                  <span className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                    <SparkIcon className="h-3 w-3" /> Level {c.level}
                  </span>
                )}
                {c.points != null && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">{c.points} pts</span>}
                {c.location && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">{c.location}</span>}
              </div>
            )}

            {c.about && <p className="mt-3 line-clamp-3 text-[12.5px] leading-relaxed text-slate-500">{c.about}</p>}

            <button
              type="button"
              onClick={() => onStartChat(c)}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-[13px] font-extrabold uppercase tracking-wide text-white shadow-md transition hover:brightness-110 active:scale-[0.98]"
              style={brandGradient(primary)}
            >
              <SendIcon className="h-4 w-4" />
              Start conversation
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------- greeting + people list */
  const isContacts = mode === "contacts";
  const firstName = (member?.name || "").split(" ")[0];

  return (
    <div className="chat-fade flex h-full flex-col overflow-y-auto px-5 py-8 sm:px-8">
      <div className="mx-auto flex w-full max-w-xl flex-col">
        {/* hero */}
        <div className="flex flex-col items-center text-center">
          {member?.name ? (
            <Avatar name={member.name} img={member.img} primary={primary} size={56} gradientRing />
          ) : (
            <span
              className="flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm"
              style={{ background: `linear-gradient(135deg, ${primary}24 0%, ${primary}0a 100%)`, color: primary }}
            >
              <ChatGlyph className="h-7 w-7" width="1.4" />
            </span>
          )}
          <h3 className="mt-4 text-[24px] font-extrabold leading-tight tracking-tight text-slate-900">
            {isContacts ? "Who do you want to reach?" : firstName ? `Hi ${firstName} 👋` : "Your messages"}
          </h3>
          <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-slate-400">
            {isContacts
              ? "Choose a community on the left to see who you can message, then pick someone here."
              : "Choose a conversation on the left, or start a new one with anyone in this membership."}
          </p>
        </div>

        {/* scope shortcut — jump straight into the scoped New-message picker */}
        {!isContacts && onScopeStart && (
          <div className="mt-7 rounded-2xl border border-slate-200/70 bg-surface p-4 shadow-sm" data-testid="chat-scope-landing">
            <p className="mb-2.5 flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-400">
              <SparkIcon className="h-3.5 w-3.5" style={{ color: primary }} />
              Start a conversation in…
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {/* Courses | Groups toggle, matching the Members page filter and
                  the New-message picker. */}
              <div className="inline-flex shrink-0 items-center rounded-xl bg-slate-100 p-0.5">
                {[["courses", "Courses"], ["groups", "Communities"]].map(([key, lab]) => {
                  const on = scopeMode === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setScopeMode(key)}
                      className={`rounded-lg px-2.5 py-1.5 text-[12px] font-bold transition ${on ? "bg-surface shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                      style={on ? { color: primary } : undefined}
                    >
                      {lab}
                    </button>
                  );
                })}
              </div>
              <ScopeSelect
                value=""
                onChange={(v) => onScopeStart(v, scopeMode)}
                options={
                  scopeMode === "courses"
                    ? contactCourses.map((c) => ({ key: String(c.course_page_id), name: c.name, member_count: c.member_count }))
                    : contactGroups.map((g) => ({ key: g.group_token, name: g.name, member_count: g.member_count }))
                }
                placeholder={scopeMode === "courses" ? "Select course" : "Select community"}
                primary={primary}
              />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {scopeMode === "courses"
                ? "You can message members who have a course you have."
                : "You can message members of a community you're in."}
            </p>
          </div>
        )}

        {/* people */}
        {suggestions.length > 0 ? (
          <div className="mt-8">
            <div className="mb-2.5 flex items-center justify-between gap-2 px-1">
              <p className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-400">
                <SparkIcon className="h-3.5 w-3.5" style={{ color: primary }} />
                Start a conversation
              </p>
              {!isContacts && contacts.length > suggestions.length && (
                <button type="button" onClick={onOpenContacts} className="text-[11px] font-bold transition hover:underline" style={{ color: primary }}>
                  See all
                </button>
              )}
            </div>
            <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-surface shadow-sm">
              {suggestions.map((c, i) => {
                const online = onlineIds.has(Number(c.other_id));
                return (
                  <button
                    key={c.member_token}
                    type="button"
                    onClick={() => onStartChat(c)}
                    className={`group flex w-full items-center gap-3 px-3.5 py-3 text-left transition hover:bg-slate-50 ${
                      i ? "border-t border-slate-100" : ""
                    }`}
                  >
                    <Avatar name={c.name} img={c.img} primary={primary} size={44} ring online={online} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-bold text-slate-800">{c.name}</span>
                      <span className="block truncate text-[11px] font-medium text-slate-400">
                        {online ? <span className="text-emerald-500">Active now</span> : "Say hello 👋"}
                      </span>
                    </span>
                    <span
                      className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-extrabold transition group-hover:brightness-110"
                      style={{ backgroundColor: `${primary}14`, color: primary }}
                    >
                      <SendIcon className="h-3.5 w-3.5" />
                      Message
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          !isContacts && (
            <div className="mt-7 flex justify-center">
              <button
                type="button"
                onClick={onOpenContacts}
                className="flex items-center gap-2 rounded-2xl px-5 py-3 text-[13px] font-extrabold text-white shadow-md transition hover:brightness-110"
                style={brandGradient(primary)}
              >
                <SearchIcon className="h-4 w-4" /> Find someone to message
              </button>
            </div>
          )
        )}

        <p className="mt-7 text-center text-[11px] text-slate-300 dark:text-slate-500">
          Messages are private between you and the member you're chatting with.
        </p>
      </div>
    </div>
  );
}
